<script setup>
import { computed, onMounted, onUnmounted, ref, nextTick, watch } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useRouter, useRoute } from 'vue-router';
import { useTimeoutPoll } from '@vueuse/core';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { debounce } from '@chatwoot/utils';
import { useAccount } from 'dashboard/composables/useAccount';

import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import BulkSelectBar from 'dashboard/components-next/captain/assistant/BulkSelectBar.vue';
import ResponseFilter from 'dashboard/components-next/captain/assistant/ResponseFilter.vue';
import DeleteDialog from 'dashboard/components-next/captain/pageComponents/DeleteDialog.vue';
import BulkDeleteDialog from 'dashboard/components-next/captain/pageComponents/BulkDeleteDialog.vue';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import CaptainPaywall from 'dashboard/components-next/captain/pageComponents/Paywall.vue';
import ResponseCard from 'dashboard/components-next/captain/assistant/ResponseCard.vue';
import CreateResponseDialog from 'dashboard/components-next/captain/pageComponents/response/CreateResponseDialog.vue';
import ChunksLauncher from 'dashboard/components-next/captain/pageComponents/response/ChunksLauncher.vue';
import ResponsePageEmptyState from 'dashboard/components-next/captain/pageComponents/emptyStates/ResponsePageEmptyState.vue';
import FeatureSpotlightPopover from 'dashboard/components-next/feature-spotlight/FeatureSpotlightPopover.vue';
import LimitBanner from 'dashboard/components-next/captain/pageComponents/response/LimitBanner.vue';

const router = useRouter();
const route = useRoute();
const store = useStore();
const { isOnChatwootCloud } = useAccount();
const uiFlags = useMapGetter('captainResponses/getUIFlags');
const responseMeta = useMapGetter('captainResponses/getMeta');
const responses = useMapGetter('captainResponses/getRecords');
const isFetching = computed(() => uiFlags.value.fetchingList);
const trainingDocuments = useMapGetter('captainTrainingDocuments/getRecords');
const FAILED_BANNER_TIMEOUT_MS = 7000;
const now = ref(Date.now());
const isFailedBannerExpired = document =>
  now.value - document.updated_at * 1000 > FAILED_BANNER_TIMEOUT_MS;
const visibleTrainingDocuments = computed(() =>
  trainingDocuments.value?.filter(
    document =>
      document.status !== 'completed' &&
      !(document.status === 'failed' && isFailedBannerExpired(document))
  )
);

let failedBannerClock;
onMounted(() => {
  failedBannerClock = setInterval(() => {
    now.value = Date.now();
  }, 1000);
});
onUnmounted(() => clearInterval(failedBannerClock));

const selectedResponse = ref(null);
const deleteDialog = ref(null);
const bulkDeleteDialog = ref(null);
const responseFilter = ref(null);

const dialogType = ref('');
const searchQuery = ref('');
const { t } = useI18n();

const createDialog = ref(null);

const selectedAssistantId = computed(() => Number(route.params.assistantId));

const handleDelete = () => {
  deleteDialog.value.dialogRef.open();
};

const handleCreate = () => {
  dialogType.value = 'create';
  nextTick(() => createDialog.value.dialogRef.open());
};

const handleEdit = () => {
  dialogType.value = 'edit';
  nextTick(() => createDialog.value.dialogRef.open());
};

const handleApprove = async response => {
  try {
    await store.dispatch('captainResponses/update', {
      id: response.id,
      status: 'approved',
    });
    useAlert(t('CAPTAIN.RESPONSES.EDIT.APPROVE_SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error?.message || t('CAPTAIN.RESPONSES.EDIT.ERROR_MESSAGE'));
  }
};

const handleTogglePause = async response => {
  try {
    await store.dispatch('captainResponses/update', {
      id: response.id,
      enabled: !response.enabled,
    });
    useAlert(
      t(
        response.enabled
          ? 'CAPTAIN.RESPONSES.EDIT.PAUSE_SUCCESS_MESSAGE'
          : 'CAPTAIN.RESPONSES.EDIT.REACTIVATE_SUCCESS_MESSAGE'
      )
    );
  } catch (error) {
    useAlert(error?.message || t('CAPTAIN.RESPONSES.EDIT.ERROR_MESSAGE'));
  }
};

const handleAction = ({ action, id }) => {
  selectedResponse.value = responses.value.find(response => id === response.id);
  nextTick(() => {
    if (action === 'delete') {
      handleDelete();
    }
    if (action === 'edit') {
      handleEdit();
    }
    if (action === 'approve') {
      handleApprove(selectedResponse.value);
      selectedResponse.value = null;
    }
    if (action === 'pause' || action === 'reactivate') {
      handleTogglePause(selectedResponse.value);
      selectedResponse.value = null;
    }
  });
};

const handleNavigationAction = ({ id, type }) => {
  if (type === 'Conversation') {
    router.push({
      name: 'inbox_conversation',
      params: { conversation_id: id },
    });
  }
};

const handleCreateClose = () => {
  dialogType.value = '';
  selectedResponse.value = null;
};

const updateURLWithFilters = (page, search) => {
  const query = {
    page: page || 1,
  };

  if (search) {
    query.search = search;
  }

  router.replace({ query });
};

const fetchResponses = (page = 1) => {
  const filterParams = responseFilter.value?.buildParams(page) || { page };

  if (selectedAssistantId.value) {
    filterParams.assistantId = selectedAssistantId.value;
  }
  if (searchQuery.value) {
    filterParams.search = searchQuery.value;
  }

  // Update URL with current filters
  updateURLWithFilters(page, searchQuery.value);

  store.dispatch('captainResponses/get', filterParams);
};

const onFiltersChanged = () => {
  fetchResponses(1);
};

const fetchTrainingDocuments = () => {
  if (!selectedAssistantId.value) return;

  store.dispatch('captainTrainingDocuments/get', {
    assistantId: selectedAssistantId.value,
  });
};

// Bulk action
const bulkSelectedIds = ref(new Set());
const hoveredCard = ref(null);

const hasPendingSelected = computed(() =>
  responses.value?.some(
    response =>
      bulkSelectedIds.value.has(response.id) && response.status === 'pending'
  )
);

const buildSelectedCountLabel = computed(() => {
  const count = responses.value?.length || 0;
  const isAllSelected = bulkSelectedIds.value.size === count && count > 0;
  return isAllSelected
    ? t('CAPTAIN.RESPONSES.UNSELECT_ALL', { count })
    : t('CAPTAIN.RESPONSES.SELECT_ALL', { count });
});

const selectedCountLabel = computed(() => {
  return t('CAPTAIN.RESPONSES.SELECTED', {
    count: bulkSelectedIds.value.size,
  });
});

const handleCardHover = (isHovered, id) => {
  hoveredCard.value = isHovered ? id : null;
};

const handleCardSelect = id => {
  const selected = new Set(bulkSelectedIds.value);
  selected[selected.has(id) ? 'delete' : 'add'](id);
  bulkSelectedIds.value = selected;
};

const fetchResponseAfterBulkAction = () => {
  const hasNoResponsesLeft = responses.value?.length === 0;
  const currentPage = responseMeta.value?.page;

  if (hasNoResponsesLeft) {
    // Page is now empty after bulk action.
    // Fetch the previous page if not already on the first page.
    const pageToFetch = currentPage > 1 ? currentPage - 1 : currentPage;
    fetchResponses(pageToFetch);
  } else {
    // Page still has responses left, re-fetch the same page.
    fetchResponses(currentPage);
  }

  // Clear selection
  bulkSelectedIds.value = new Set();
};

const onPageChange = page => {
  const hadSelection = bulkSelectedIds.value.size > 0;

  fetchResponses(page);

  if (hadSelection) {
    bulkSelectedIds.value = new Set();
  }
};

const onDeleteSuccess = () => {
  if (responses.value?.length === 0 && responseMeta.value?.page > 1) {
    onPageChange(responseMeta.value.page - 1);
  }
};

const onBulkDeleteSuccess = () => {
  fetchResponseAfterBulkAction();
};

const handleBulkApprove = async () => {
  try {
    await store.dispatch(
      'captainBulkActions/handleBulkApprove',
      Array.from(bulkSelectedIds.value)
    );

    fetchResponseAfterBulkAction();
    useAlert(t('CAPTAIN.RESPONSES.BULK_APPROVE.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.message || t('CAPTAIN.RESPONSES.BULK_APPROVE.ERROR_MESSAGE')
    );
  }
};

const debouncedSearch = debounce(async () => {
  fetchResponses(1);
}, 500);

const initializeFromURL = () => {
  if (route.query.search) {
    searchQuery.value = route.query.search;
  }
  const pageFromURL = parseInt(route.query.page, 10) || 1;
  fetchResponses(pageFromURL);
};

// Metadata + embedding generation for a newly created FAQ, and chunk generation for
// an uploaded training document, both run in background jobs. Poll quietly while
// any response is still "processing" or any training document is still
// pending/processing, so the UI updates without a manual page reload.
const EMBEDDING_POLL_INTERVAL_MS = 4000;

const hasProcessingResponses = computed(() =>
  responses.value?.some(response => response.embedding_status === 'processing')
);

const hasPendingTrainingDocuments = computed(() =>
  trainingDocuments.value?.some(
    document =>
      document.status === 'pending' || document.status === 'processing'
  )
);

const hasProcessingItems = computed(
  () => hasProcessingResponses.value || hasPendingTrainingDocuments.value
);

const { pause: pauseEmbeddingPolling, resume: resumeEmbeddingPolling } =
  useTimeoutPoll(
    () => {
      fetchResponses(responseMeta.value?.page);
      fetchTrainingDocuments();
    },
    EMBEDDING_POLL_INTERVAL_MS,
    { immediate: false }
  );

watch(
  hasProcessingItems,
  isProcessing => {
    if (isProcessing) {
      resumeEmbeddingPolling();
    } else {
      pauseEmbeddingPolling();
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  pauseEmbeddingPolling();
});

onMounted(() => {
  initializeFromURL();
  fetchTrainingDocuments();
});
</script>

<template>
  <PageLayout
    :total-count="responseMeta.totalCount"
    :current-page="responseMeta.page"
    :button-policy="['administrator']"
    :header-title="$t('CAPTAIN.RESPONSES.HEADER')"
    :button-label="$t('CAPTAIN.RESPONSES.ADD_NEW')"
    :is-fetching="isFetching"
    :is-empty="!responses.length"
    :show-pagination-footer="!isFetching && !!responses.length"
    :feature-flag="FEATURE_FLAGS.CAPTAIN"
    @update:current-page="onPageChange"
    @click="handleCreate"
  >
    <template #knowMore>
      <FeatureSpotlightPopover
        :button-label="$t('CAPTAIN.HEADER_KNOW_MORE')"
        :title="$t('CAPTAIN.RESPONSES.EMPTY_STATE.FEATURE_SPOTLIGHT.TITLE')"
        :note="$t('CAPTAIN.RESPONSES.EMPTY_STATE.FEATURE_SPOTLIGHT.NOTE')"
        :hide-actions="!isOnChatwootCloud"
        fallback-thumbnail="/assets/images/dashboard/captain/faqs-popover-light.svg"
        fallback-thumbnail-dark="/assets/images/dashboard/captain/faqs-popover-dark.svg"
        learn-more-url="https://chwt.app/captain-faq"
      />
    </template>

    <template #search>
      <div
        v-if="bulkSelectedIds.size === 0"
        class="flex gap-3 justify-between w-full items-center"
      >
        <Input
          v-model="searchQuery"
          :placeholder="$t('CAPTAIN.RESPONSES.SEARCH_PLACEHOLDER')"
          class="w-64"
          size="sm"
          type="search"
          autofocus
          @input="debouncedSearch"
        />
      </div>
    </template>

    <template #subHeader>
      <ResponseFilter
        v-show="!bulkSelectedIds.size"
        ref="responseFilter"
        :assistant-id="selectedAssistantId"
        class="mb-2"
        @change="onFiltersChanged"
      />
      <BulkSelectBar
        v-model="bulkSelectedIds"
        :all-items="responses"
        :select-all-label="buildSelectedCountLabel"
        :selected-count-label="selectedCountLabel"
        :delete-label="$t('CAPTAIN.RESPONSES.BULK_DELETE_BUTTON')"
        class="w-fit"
        :class="{
          'mb-2': bulkSelectedIds.size > 0,
        }"
        @bulk-delete="bulkDeleteDialog.dialogRef.open()"
      >
        <template v-if="hasPendingSelected" #secondaryActions>
          <Button
            :label="$t('CAPTAIN.RESPONSES.BULK_APPROVE_BUTTON')"
            sm
            ghost
            icon="i-lucide-check"
            class="!px-1.5"
            @click="handleBulkApprove"
          />
        </template>
      </BulkSelectBar>
    </template>

    <template #emptyState>
      <ResponsePageEmptyState @click="handleCreate" />
    </template>

    <template #paywall>
      <CaptainPaywall />
    </template>

    <template #body>
      <LimitBanner class="mb-5" />
      <Banner
        v-for="document in visibleTrainingDocuments"
        :key="document.id"
        :color="document.status === 'failed' ? 'ruby' : 'amber'"
        class="mb-4"
      >
        {{
          document.status === 'failed'
            ? $t('CAPTAIN.TRAINING_DOCUMENTS.STATUS_BANNER.FAILED', {
                filename: document.filename,
                error: document.error_message,
              })
            : $t('CAPTAIN.TRAINING_DOCUMENTS.STATUS_BANNER.PROCESSING', {
                filename: document.filename,
              })
        }}
      </Banner>

      <div class="flex flex-col gap-4">
        <ResponseCard
          v-for="response in responses"
          :id="response.id"
          :key="response.id"
          :question="response.question"
          :answer="response.answer"
          :documentable="response.documentable"
          :status="response.status"
          :embedding-status="response.embedding_status"
          :enabled="response.enabled"
          :created-via="response.created_via"
          :action-url="response.action_url"
          :media-url="response.media_url"
          :attachments="response.attachments"
          :created-at="response.created_at"
          :updated-at="response.updated_at"
          :is-selected="bulkSelectedIds.has(response.id)"
          :selectable="hoveredCard === response.id || bulkSelectedIds.size > 0"
          :show-menu="!bulkSelectedIds.has(response.id)"
          :show-actions="false"
          @action="handleAction"
          @navigate="handleNavigationAction"
          @select="handleCardSelect"
          @hover="isHovered => handleCardHover(isHovered, response.id)"
        />
      </div>
    </template>

    <DeleteDialog
      v-if="selectedResponse"
      ref="deleteDialog"
      :entity="selectedResponse"
      type="Responses"
      @delete-success="onDeleteSuccess"
    />

    <BulkDeleteDialog
      v-if="bulkSelectedIds"
      ref="bulkDeleteDialog"
      :bulk-ids="bulkSelectedIds"
      type="AssistantResponse"
      @delete-success="onBulkDeleteSuccess"
    />

    <CreateResponseDialog
      v-if="dialogType"
      ref="createDialog"
      :type="dialogType"
      :selected-response="selectedResponse"
      @close="handleCreateClose"
    />
  </PageLayout>

  <ChunksLauncher />
</template>
